import { SWRConfig } from 'swr';
import Head from 'next/head';
import SEO from '@seo/index';
import { fetchGoalInfo } from '@api/fetchers/goal';
import { getKey as getTopologyKey } from '@api/hooks/topology/useTopology';
import resolvePromiseToObject from '@utils/resolvePromiseToObj';
import CourseContainer from '@cont/CertificationCourses';
import fetchPageMetaInfo from '@api/fetchers/pageMetaInfo';
import { fetchCertificationCourseData } from '@api/fetchers/CertificationCourses';
import useFullCertificationCourseData from '@cont/CertificationCourses/useFullCertificationCourseData';
import { PROGRAM_LANDING_PAGE } from '@cont/CertificationCourses/constants';
import { usePageSeoContent } from '@stores/AppCommonData';
import SimpleLayoutWithFooter from '@cont/CertificationCourses/SimpleLayoutWithFooter/SimpleLayoutWithFooter';

const CertificationCourse = ({
  goalUID,
  courseSlug,
  goalInfo,
  fallbackData
}) => {
  const { title: metaTitle, description: metaDescription } =
    usePageSeoContent();
  const { keys } = useFullCertificationCourseData(goalUID, fallbackData);
  return (
    <SWRConfig
      value={{
        revalidateOnMount: false,
        dedupingInterval: 60 * 1000,
        fallback: {
          [keys.certificationCourseDataSWRKey]:
            fallbackData?.certificationCourseData
        }
      }}
    >
      <SEO
        title={metaTitle}
        description={metaDescription}
        openGraph={{
          title: metaTitle,
          description: { metaDescription }
        }}
      />
      <Head>
        <title>{metaTitle}</title>
      </Head>
      <CourseContainer
        goalUID={goalUID}
        courseSlug={courseSlug}
        goalInfo={goalInfo}
      />
    </SWRConfig>
  );
};

CertificationCourse.pageInfo = {
  name: PROGRAM_LANDING_PAGE,
  isMWebSupported: true,
  layoutOptions: {
    hideFreshbot: true,
    disableDarkMode: true
  }
};

export const getStaticProps = async ({ params, preview }) => {
  try {
    const { goalUID, courseSlug } = params || {};
    const goalInfo = await fetchGoalInfo(goalUID);
    const promises = [];
    promises.push(
      resolvePromiseToObject(
        fetchCertificationCourseData(goalUID, preview),
        'certificationCourseData'
      )
    );
    promises.push(
      resolvePromiseToObject(
        fetchPageMetaInfo(PROGRAM_LANDING_PAGE, goalUID, preview),
        'metaInfo'
      )
    );
    const fallbackData = await Promise.all(promises).then((resolvedValues) =>
      resolvedValues.reduce(
        (acc, next) => ({ ...acc, [next.type]: next.result }),
        {}
      )
    );
    /**
     * If the course slug present in the currect URL is not same as
     * the one provided by the CMS permanently redirect it with the correct slug.
     */
    const correctCourseSlug =
      fallbackData?.certificationCourseData?.overAll?.programmeSlug;
    if (correctCourseSlug && courseSlug !== correctCourseSlug) {
      return {
        redirect: {
          destination: `/programmes/${correctCourseSlug}/${goalUID}`,
          permanent: true
        }
      };
    }
    return {
      props: {
        goalUID,
        goalInfo,
        courseSlug,
        fallbackData,
        pageMetaInfo: fallbackData.metaInfo || {},
        globalFallbackData: {
          [getTopologyKey(goalInfo.uid)]: goalInfo
        }
      }
    };
  } catch (error) {
    return { notFound: true, revalidate: preview ? 10 : 15 * 60 };
  }
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

const Layout = ({ children }) => (
  <SimpleLayoutWithFooter
    layoutOptions={{
      showLogin: true,
      hideHeaderShadow: true
    }}
    showFooter
  >
    {children}
  </SimpleLayoutWithFooter>
);

export default CertificationCourse;
CertificationCourse.Layout = Layout;
CertificationCourse.Wrapper = null;
